/**
 * The disruption meter (AGENTS.md section 10.5).
 *
 * With no "enable" flag on the device, THE PATTERN ITSELF is the only thing that
 * can make the dongle unpleasant. This is the user's only warning before they save
 * a pattern they will have to unplug to escape, so it belongs next to Save.
 *
 * MACRO path length is exact: it is a sum over the step list.
 *
 * Generator figures are ANALYTIC APPROXIMATIONS derived from the same parameters
 * the firmware uses, not a simulation. That is a deliberate trade: mirroring the
 * generator algorithms here would duplicate firmware logic and drift from it
 * silently. The numbers are close enough for a warning band with two-orders-of-
 * magnitude spacing, and they are computed over one full cycle (64 steps) which is
 * exact for CIRCLE and ZIGZAG, whose paths are closed.
 */

import { CFG_MAX_AMPLITUDE, CFG_MIN_STEP_MS, PatternKind } from '../protocol/constants.ts';
import type { Draft } from './draft.ts';
import type { JigStep } from '../protocol/types.ts';

/** One full generator cycle is 64 steps (GEN_STEPS_PER_PERIOD in Pattern.c). */
const GEN_STEPS_PER_PERIOD = 64;

export type DisruptionBand = 'green' | 'amber' | 'red';

export interface Disruption {
  /** Cursor path length in px per second. */
  pxPerSec: number;
  /** Path length of one full cycle, px. */
  cyclePathPx: number;
  /** Duration of one full cycle, ms. */
  cycleMs: number;
  band: DisruptionBand;
  /** True when the figure is an analytic estimate rather than a sum. */
  estimated: boolean;
}

/**
 * Bands. The spacing is deliberately wide: this is a "will it fight me?" warning,
 * not a measurement.
 */
export const BAND_AMBER_PX_PER_SEC = 2;
export const BAND_RED_PX_PER_SEC = 20;

export function bandFor(pxPerSec: number): DisruptionBand {
  if (pxPerSec < BAND_AMBER_PX_PER_SEC) return 'green';
  if (pxPerSec < BAND_RED_PX_PER_SEC) return 'amber';
  return 'red';
}

export const BAND_TEXT: Readonly<Record<DisruptionBand, string>> = Object.freeze({
  green: 'Invisible in normal use.',
  amber: 'Noticeable — it will interfere with precision work.',
  red: 'This WILL fight you whenever it runs. You may have to unplug to escape it.',
});

/** Exact path length of one macro cycle, px. */
export function macroCyclePathPx(steps: readonly JigStep[]): number {
  return steps.reduce((sum, s) => sum + Math.abs(s.dx) + Math.abs(s.dy), 0);
}

/** Exact cycle duration of a macro, ms, honouring the device's 2 ms floor. */
export function macroCycleMs(steps: readonly JigStep[]): number {
  return steps.reduce((sum, s) => sum + Math.max(s.delayMs, CFG_MIN_STEP_MS), 0);
}

export function computeDisruption(draft: Draft): Disruption {
  if (draft.kind === PatternKind.MACRO) {
    const cyclePathPx = macroCyclePathPx(draft.steps);
    const cycleMs = macroCycleMs(draft.steps);
    const pxPerSec = cycleMs > 0 ? (cyclePathPx * 1000) / cycleMs : 0;
    return { pxPerSec, cyclePathPx, cycleMs, band: bandFor(pxPerSec), estimated: false };
  }

  const amp = clampAmplitude(draft.amplitude);
  const periodMs = draft.periodMs > 0 ? draft.periodMs : 1000;
  let cyclePathPx: number;

  switch (draft.kind) {
    case PatternKind.CIRCLE:
      // Radius is `amplitude`, so one revolution is 2*pi*amplitude.
      cyclePathPx = 2 * Math.PI * amp;
      break;

    case PatternKind.ZIGZAG:
    case PatternKind.DRIFT:
      // Out and back across the full +/- amplitude box: 2*amp each way.
      cyclePathPx = 4 * amp;
      break;

    case PatternKind.RANDWALK: {
      // Per axis the step is uniform over {-s, 0, +s}, so E|d| = 2s/3. Two axes give
      // 4s/3 per step, and the walk reflects at the bound so it cannot exceed it --
      // giving up on the exact figure overestimates rather than under, which is the
      // safe direction for a warning.
      const s = Math.max(1, draft.jitter || 1);
      cyclePathPx = GEN_STEPS_PER_PERIOD * (4 * s) / 3;
      break;
    }

    default:
      cyclePathPx = 0;
  }

  const pxPerSec = (cyclePathPx * 1000) / periodMs;
  return { pxPerSec, cyclePathPx, cycleMs: periodMs, band: bandFor(pxPerSec), estimated: true };
}

function clampAmplitude(amplitude: number): number {
  if (!Number.isFinite(amplitude) || amplitude <= 0) return 1;
  return Math.min(amplitude, CFG_MAX_AMPLITUDE);
}

/** Human-readable, with sensible precision at both ends of the range. */
export function formatPxPerSec(pxPerSec: number): string {
  if (pxPerSec === 0) return '0 px/s';
  if (pxPerSec < 0.1) return `${pxPerSec.toFixed(3)} px/s`;
  if (pxPerSec < 10) return `${pxPerSec.toFixed(2)} px/s`;
  return `${pxPerSec.toFixed(1)} px/s`;
}
